import { useEditor, type AspectKey } from '../../state/editorStore';
import { saveEdits } from '../../lib/pipeline';

/** Output aspect ratio picker (portrait / landscape / square / original). The
 *  ratio is auto-detected from the source at ingest; this lets the user switch
 *  it. Drives both the stage preview and the export. */
const OPTIONS: { value: AspectKey; label: string }[] = [
  { value: '9:16', label: '9:16 · Portrait' },
  { value: '4:5', label: '4:5 · Portrait' },
  { value: '1:1', label: '1:1 · Square' },
  { value: '16:9', label: '16:9 · Landscape' },
  { value: 'source', label: 'Original' },
];

export function StudioAspectSelect() {
  const aspect = useEditor((s) => s.aspect);
  const projectId = useEditor((s) => s.projectId);
  const setAspect = useEditor((s) => s.setAspect);

  const onChange = (value: AspectKey) => {
    setAspect(value);
    // Persist immediately so a reload keeps the chosen ratio.
    if (projectId) saveEdits().catch(() => undefined);
  };

  return (
    <label className="st-aspect" title="Output aspect ratio (preview + export)">
      <RatioGlyph aspect={aspect} />
      <select
        className="st-aspect-select"
        value={aspect}
        disabled={!projectId}
        onChange={(e) => onChange(e.target.value as AspectKey)}
        aria-label="Output aspect ratio"
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** A tiny rectangle drawn in the current ratio, so the shape is readable at a
 *  glance next to the dropdown. */
function RatioGlyph({ aspect }: { aspect: AspectKey }) {
  const map: Record<AspectKey, { w: number; h: number }> = {
    '9:16': { w: 9, h: 16 },
    '4:5': { w: 8, h: 10 },
    '1:1': { w: 12, h: 12 },
    '16:9': { w: 16, h: 9 },
    source: { w: 12, h: 12 },
  };
  const { w, h } = map[aspect];
  const scale = 15 / Math.max(w, h);
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden style={{ flex: '0 0 auto' }}>
      <rect
        x={(16 - w * scale) / 2}
        y={(16 - h * scale) / 2}
        width={w * scale}
        height={h * scale}
        rx={2}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      />
    </svg>
  );
}
